import React from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Typography,
  Box,
  Button,
  LinearProgress,
  Stack,
  Alert,
  AlertTitle
} from '@mui/material';
import { HealthStatusBadge } from './HealthStatusBadge';
import type { DataSourceHealth, DataSourceType } from '../../types/dataHealth.types';

interface DataSourceHealthCardProps {
  health: DataSourceHealth;
  onForceSync: (sourceType: DataSourceType) => void;
}

const sourceIcons: Record<DataSourceType, { icon: string; name: string }> = {
  GSC: { icon: '🔍', name: 'Google Search Console' },
  BING: { icon: '🅱️', name: 'Bing Webmaster' },
  GA4: { icon: '📊', name: 'Google Analytics 4' },
  CRAWL_LOG: { icon: '🕷️', name: 'Crawl Logs' }
};

const formatRelativeTime = (isoString: string | null): string => {
  if (!isoString) return 'Never';
  
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  return 'Just now';
};

const formatDate = (dateString: string | null): string => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

export const DataSourceHealthCard: React.FC<DataSourceHealthCardProps> = ({
  health,
  onForceSync
}) => {
  const source = sourceIcons[health.sourceType];
  const isStaleOrFailed = health.status === 'STALE' || health.status === 'FAILED';
  
  // Determine lag context
  const expectedLagHours: Record<DataSourceType, number> = {
    GSC: 72,
    BING: 72,
    GA4: 48,
    CRAWL_LOG: 24
  };
  
  const isWithinExpected = health.lagHours <= expectedLagHours[health.sourceType];
  const lagLabel = isWithinExpected ? `${health.lagHours}h (Expected)` : `${health.lagHours}h`;

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderLeft: 4,
        borderColor: isStaleOrFailed ? 'error.main' : 'success.main'
      }}
    >
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="h4" component="span">
              {source.icon}
            </Typography>
            <Typography variant="h6" component="span">
              {source.name}
            </Typography>
          </Box>
        }
        subheader={<HealthStatusBadge status={health.status} size="medium" />}
      />

      <CardContent sx={{ flexGrow: 1 }}>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">
              Last Sync:
            </Typography>
            <Typography variant="body2" fontWeight="medium">
              {formatRelativeTime(health.lastSuccessfulSync)}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">
              Last Data Date:
            </Typography>
            <Typography variant="body2" fontWeight="medium">
              {formatDate(health.lastDataDate)}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">
              Data Lag:
            </Typography>
            <Typography
              variant="body2"
              fontWeight="medium"
              color={isWithinExpected ? 'success.main' : 'warning.main'}
            >
              {lagLabel}
            </Typography>
          </Box>

          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Coverage:
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {health.coveragePercent.toFixed(0)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={health.coveragePercent}
              sx={{ height: 6, borderRadius: 1 }}
            />
          </Box>

          {health.errorCount > 0 && health.errorDetails && (
            <Alert severity="error" variant="outlined">
              <AlertTitle>Sync Errors</AlertTitle>
              {health.errorCount} error{health.errorCount > 1 ? 's' : ''} in last 24h
              {health.errorDetails.message && (
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  {health.errorDetails.message}
                </Typography>
              )}
            </Alert>
          )}
        </Stack>
      </CardContent>

      <CardActions>
        <Button
          variant="outlined"
          size="small"
          fullWidth
          onClick={() => onForceSync(health.sourceType)}
          disabled={health.status === 'HEALTHY'}
        >
          Force Sync
        </Button>
      </CardActions>
    </Card>
  );
};
